import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  ClipboardText, Clock, Eye, CheckCircle, Warning,
  ArrowRight, PaperPlaneTilt, FloppyDisk, X, Lightning,
  ListChecks, FolderOpen, Paperclip, DownloadSimple, UploadSimple, ChatCircleText,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import EntreprisePJUpload from '../components/entreprise/EntreprisePJUpload';
import EntrepriseDocumentsList from '../components/entreprise/EntrepriseDocumentsList';
import ConsultationQuestions from '../components/entreprise/ConsultationQuestions';
import CodeVerificationDialog, { CODE_HEADER, verificationRequise } from '../components/shared/CodeVerificationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A6 (P2#16) — même numéro de lot que la DPGF de l'architecte (« Lot 03 »)
const numLot = (lot, idx) => lot?.numero || String(idx + 1).padStart(2, '0');
const libNum = (n) => (/^\d+$/.test(String(n || '')) ? `Lot ${n}` : (n || ''));
// A6 (P2#9) — résultat après attribution
const RESULTAT = {
  retenu: { label: 'Retenu', cls: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' },
  non_retenu: { label: 'Non retenu', cls: 'bg-[#F5F5F3] text-[#6B6B6B] border-[#9A9A9A]' },
};

const STATUT_CONFIG = {
  EN_ATTENTE: { label: 'En attente', color: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F3]', icon: Clock },
  VU: { label: 'Consultation ouverte', color: 'text-[#1E3A5F]', bg: 'bg-[#E3F2FD]', icon: Eye },
  REPONDU: { label: 'Offre soumise', color: 'text-[#2D6A4F]', bg: 'bg-[#E8F5E9]', icon: CheckCircle },
  REFUSE: { label: 'Déclinée', color: 'text-[#8B3A3A]', bg: 'bg-[#FFEBEE]', icon: Warning },
};

// Sprint C #18 — Décompte date limite
function DeadlineCountdown({ dateLimite }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);
  if (!dateLimite) return null;
  const limit = new Date(dateLimite).getTime();
  const diff = limit - now;
  const expired = diff < 0;
  const days = Math.floor(Math.abs(diff) / 86400000);
  const hours = Math.floor((Math.abs(diff) % 86400000) / 3600000);
  let label, classes;
  if (expired) {
    label = `Délai expiré (${days}j ${hours}h depuis)`;
    classes = 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]';
  } else if (days < 3) {
    label = days === 0 ? `${hours}h restantes` : `${days}j ${hours}h restants`;
    classes = 'bg-[#FFF3E0] text-[#C4773B] border-[#C4773B]';
  } else {
    label = `${days}j restants`;
    classes = 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]';
  }
  return (
    <div className={`inline-flex flex-col items-end px-3 py-2 border ${classes}`} data-testid="entreprise-deadline-countdown">
      <span className="text-[10px] uppercase tracking-wider opacity-75">Date limite</span>
      <span className="text-sm font-bold">{new Date(dateLimite).toLocaleDateString('fr-FR')}</span>
      <span className="text-[10px] font-medium">{label}</span>
    </div>
  );
}

export default function EntrepriseConsultations() {
  const { user } = useAuth();
  // A6 (P2#8) — ouverture directe depuis le tableau de bord / « Mes actions » : ?invitation=ID
  const [searchParams, setSearchParams] = useSearchParams();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConsultation, setActiveConsultation] = useState(null);
  const [dpgfData, setDpgfData] = useState(null);
  const [tab, setTab] = useState('chiffrage');
  // États formulaire
  const [lignes, setLignes] = useState([]);
  const [lotsRefuses, setLotsRefuses] = useState(new Set());
  // TVA Sprint — Iter 79 : la TVA est imposée par l'architecte via le snapshot DCE.
  // On la lit en lecture seule depuis lot.tva_pct, plus de state modifiable côté entreprise.
  const [variantes, setVariantes] = useState({}); // {lot_id: { actif: bool, description: string, montant_ht_total: number }}
  const [noteGlobale, setNoteGlobale] = useState('');
  const [memoireTechnique, setMemoireTechnique] = useState('');
  const [delaiJours, setDelaiJours] = useState('');
  const [saving, setSaving] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  // RP10 (D10 C) — session ouverte par lien : code SMS / e-mail pour envoyer l'offre
  const [codeOpen, setCodeOpen] = useState(false);
  // RP63-64 (D27 B) — réponse en Excel : import contrôlé qui pré-remplit l'offre
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fichierRef = useRef(null);

  const fetchConsultations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/entreprise/consultations`, { withCredentials: true });
      setConsultations(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchConsultations(); }, [fetchConsultations]);

  const reloadDpgf = useCallback(async (invitationId) => {
    const { data } = await axios.get(`${API_URL}/api/entreprise/consultations/${invitationId}/dpgf`, { withCredentials: true });
    setDpgfData(data);
    if (data.reponse?.lignes?.length) {
      setLignes(data.reponse.lignes);
    } else {
      // TVA Sprint — Iter 79 : init tva_pct depuis le snapshot DCE (imposé par l'archi)
      const all = data.lots.flatMap((l) => {
        const lotTva = (typeof l.tva_pct === 'number') ? l.tva_pct : 20;
        return l.postes.map((p) => ({ poste_id: p.id, prix_unitaire_ht: 0, tva_pct: lotTva, commentaire: '' }));
      });
      setLignes(all);
    }
    setLotsRefuses(new Set(data.reponse?.lots_refuses || []));
    const varsMap = {};
    (data.reponse?.variantes || []).forEach((v) => {
      varsMap[v.lot_id] = { actif: true, description: v.description || '', montant_ht_total: v.montant_ht_total || 0 };
    });
    setVariantes(varsMap);
    setNoteGlobale(data.reponse?.note_globale || '');
    setMemoireTechnique(data.reponse?.memoire_technique_libre || '');
    setDelaiJours(data.reponse?.delai_realisation_jours ? String(data.reponse.delai_realisation_jours) : '');
  }, []);

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (fichierRef.current) fichierRef.current.value = '';
    if (!file || !activeConsultation) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/entreprise/consultations/${activeConsultation.invitation_id}/import-excel`,
        fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setImportResult(data);
      if (data.ok) {
        const parPoste = Object.fromEntries((data.lignes || []).map((l) => [l.poste_id, l]));
        setLignes((prev) => prev.map((l) => (parPoste[l.poste_id]
          ? { ...l, prix_unitaire_ht: parPoste[l.poste_id].prix_unitaire_ht, commentaire: parPoste[l.poste_id].commentaire || l.commentaire }
          : l)));
        toast.success(`${data.nb_postes_chiffres} poste${data.nb_postes_chiffres > 1 ? 's' : ''} pré-rempli${data.nb_postes_chiffres > 1 ? 's' : ''} : vérifiez puis envoyez votre offre`);
      } else {
        toast.error('Le fichier contient des erreurs : rien n\'a été importé');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import impossible');
    } finally { setImporting(false); }
  };

  const openConsultation = async (consultation) => {
    try {
      await reloadDpgf(consultation.invitation_id);
      setActiveConsultation(consultation);
      setImportResult(null);
      setTab('chiffrage');
    } catch (err) { toast.error('Erreur chargement consultation'); }
  };

  useEffect(() => {
    const cible = searchParams.get('invitation');
    if (!cible || loading || activeConsultation) return;
    const c = consultations.find((x) => x.invitation_id === cible);
    if (c) openConsultation(c);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, consultations]);

  const updateLigne = (posteId, field, value) => {
    setLignes((prev) => prev.map((l) => l.poste_id === posteId ? { ...l, [field]: value } : l));
  };
  const toggleRefuserLot = (lotId) => {
    setLotsRefuses((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId); else next.add(lotId);
      return next;
    });
  };
  const toggleVariante = (lotId) => {
    setVariantes((prev) => ({
      ...prev,
      [lotId]: prev[lotId]?.actif ? { ...prev[lotId], actif: false } : { actif: true, description: prev[lotId]?.description || '', montant_ht_total: prev[lotId]?.montant_ht_total || 0 }
    }));
  };
  const updateVariante = (lotId, patch) => {
    setVariantes((prev) => ({ ...prev, [lotId]: { ...(prev[lotId] || {}), actif: true, ...patch } }));
  };

  // Calculs totals
  const totals = useMemo(() => {
    if (!dpgfData) return { ht: 0, tva: 0, ttc: 0, varHt: 0 };
    let ht = 0, tva = 0;
    for (const lot of dpgfData.lots) {
      if (lotsRefuses.has(lot.id)) continue;
      // TVA Sprint — Iter 79 : imposée par le snapshot DCE (architecte)
      const lotTva = (typeof lot.tva_pct === 'number') ? lot.tva_pct : 20;
      for (const poste of lot.postes) {
        const ligne = lignes.find((l) => l.poste_id === poste.id);
        const lineHt = (poste.quantite || 0) * (ligne?.prix_unitaire_ht || 0);
        ht += lineHt;
        tva += lineHt * lotTva / 100;
      }
    }
    let varHt = 0;
    Object.values(variantes).forEach((v) => { if (v.actif) varHt += v.montant_ht_total || 0; });
    return { ht: Math.round(ht * 100) / 100, tva: Math.round(tva * 100) / 100, ttc: Math.round((ht + tva) * 100) / 100, varHt: Math.round(varHt * 100) / 100 };
  }, [dpgfData, lignes, lotsRefuses, variantes]);

  const buildPayload = (soumettre) => ({
    lignes,
    lots_refuses: [...lotsRefuses],
    // tva_par_lot : retiré — la TVA est imposée par le snapshot DCE côté backend.
    variantes: Object.entries(variantes)
      .filter(([, v]) => v.actif)
      .map(([lot_id, v]) => ({ lot_id, description: v.description, montant_ht_total: v.montant_ht_total || 0 })),
    note_globale: noteGlobale,
    memoire_technique_libre: memoireTechnique,
    delai_realisation_jours: delaiJours ? parseInt(delaiJours, 10) : null,
    soumettre,
  });

  const handleSave = async (soumettre = false, codeVerification = null) => {
    if (soumettre && !codeVerification) {
      const lotsAChiffrer = (dpgfData?.lots || []).filter((l) => !lotsRefuses.has(l.id));
      if (!lotsAChiffrer.length) { toast.error('Vous devez chiffrer au moins un lot ou retirer le toggle "Refuser"'); return; }
      const incomplete = lotsAChiffrer.some((lot) =>
        lot.postes.some((p) => {
          const li = lignes.find((x) => x.poste_id === p.id);
          return !li || !li.prix_unitaire_ht || li.prix_unitaire_ht <= 0;
        })
      );
      if (incomplete) { toast.error('Veuillez renseigner tous les prix unitaires des lots à chiffrer (ou refuser le lot)'); return; }
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/entreprise/consultations/${activeConsultation.invitation_id}/reponse`,
        buildPayload(soumettre),
        { withCredentials: true, headers: codeVerification ? { [CODE_HEADER]: codeVerification } : {} }
      );
      toast.success(data.message || (soumettre ? 'Offre soumise' : 'Brouillon sauvegardé'));
      if (soumettre) {
        setActiveConsultation(null);
        setRecapOpen(false);
        fetchConsultations();
      } else {
        await reloadDpgf(activeConsultation.invitation_id);
      }
    } catch (err) {
      if (soumettre && verificationRequise(err)) {
        if (codeVerification) throw err; // code refusé : affiché dans la fenêtre du code
        setCodeOpen(true);
        return;
      }
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // ===== Detail view =====
  if (activeConsultation && dpgfData) {
    const isSubmitted = dpgfData.reponse?.statut === 'SOUMISE';
    const resultat = dpgfData.invitation?.resultat;
    const resultatParLot = dpgfData.invitation?.resultat_par_lot || {};
    const horsDelai = !!dpgfData.invitation?.hors_delai;

    return (
      <Layout>
        <div className="p-6" data-testid="consultation-detail">
          {/* Header */}
          <div className="flex flex-wrap items-start gap-4 mb-6">
            <Button variant="ghost" onClick={() => setActiveConsultation(null)} className="rounded-none gap-2" data-testid="consultation-back-button">
              <ArrowRight size={18} className="rotate-180" /> Retour
            </Button>
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                {activeConsultation.chantier_nom}
              </h2>
              <p className="text-sm text-[#6B6B6B]">Appel d'offres — {dpgfData.lots.map((l, i) => `${libNum(numLot(l, i))} ${l.nom}`).join(' · ')}</p>
            </div>
            <DeadlineCountdown dateLimite={dpgfData.invitation?.date_limite} />
          </div>

          {/* A6 (P2#9) — résultat après attribution */}
          {resultat && (
            <div className={`border px-4 py-3 mb-4 text-sm ${RESULTAT[resultat].cls}`} data-testid="consultation-resultat">
              <strong>{resultat === 'retenu' ? 'Retenu' : 'Non retenu'}</strong>
              {' — '}
              {dpgfData.lots.map((l, i) => {
                const r = resultatParLot[l.id];
                return `${libNum(numLot(l, i))} ${l.nom} : ${r === 'retenu' ? 'retenu' : r === 'non_retenu' ? 'non retenu' : 'en cours de décision'}`;
              }).join(' · ')}
              {resultat === 'retenu' && '. Le chantier apparaît dans vos marchés.'}
            </div>
          )}
          {/* A6 (P2#3) — date limite passée : l'offre sera marquée « hors délai » */}
          {horsDelai && !isSubmitted && (
            <div className="border border-[#C53030] bg-[#FEF2F2] px-4 py-3 mb-4 text-sm text-[#8B3A3A]" data-testid="consultation-hors-delai">
              La date limite est passée : votre offre sera transmise à l'architecte marquée « hors délai ».
            </div>
          )}

          {isSubmitted && (
            <div className="bg-[#E8F5E9] border border-[#2D6A4F] px-4 py-3 mb-4 flex items-center gap-3" data-testid="consultation-submitted-banner">
              <CheckCircle size={20} className="text-[#2D6A4F]" />
              <p className="text-sm text-[#2D6A4F] font-medium">
                Offre soumise — Total HT : {dpgfData.reponse.montant_total_ht?.toLocaleString('fr-FR')} € · TTC : {dpgfData.reponse.montant_total_ttc?.toLocaleString('fr-FR')} €
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-0 border-b border-[#E0E0DB] mb-4 overflow-x-auto" data-testid="consultation-tabs">
            {[
              { id: 'chiffrage', label: 'Chiffrage', icon: ListChecks },
              { id: 'documents', label: `Documents`, icon: FolderOpen },
              { id: 'pj', label: 'Pièces jointes', icon: Paperclip },
              // B6 — questions / réponses partagées avec toutes les entreprises consultées
              { id: 'questions', label: 'Questions et réponses', icon: ChatCircleText },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${active ? 'border-[#C75B39] text-[#C75B39] font-semibold' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
                  data-testid={`consultation-tab-${t.id}`}
                >
                  <Icon size={14} weight={active ? 'fill' : 'regular'} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === 'documents' && <EntrepriseDocumentsList invitationId={activeConsultation.invitation_id} />}
          {tab === 'questions' && (
            <ConsultationQuestions
              invitationId={activeConsultation.invitation_id}
              lots={dpgfData.lots.map((l, i) => ({ id: l.id, libelle: `${libNum(numLot(l, i))} — ${l.nom}` }))}
            />
          )}
          {tab === 'pj' && (
            <EntreprisePJUpload
              invitationId={activeConsultation.invitation_id}
              items={dpgfData.reponse?.pieces_jointes || []}
              onChanged={() => reloadDpgf(activeConsultation.invitation_id)}
              locked={isSubmitted}
            />
          )}

          {tab === 'chiffrage' && (
            <>
              {/* RP63-64 — chiffrer dans son outil : télécharger le cadre Excel, puis l'importer */}
              {!isSubmitted && (
                <div className="border border-[#E0E0DB] bg-[#FAFAF8] px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="consultation-excel">
                  <p className="text-sm text-[#2C2C2C] flex-1 min-w-[220px]">
                    Vous chiffrez dans Excel ? Téléchargez le cadre de réponse, remplissez les prix unitaires, puis importez-le :
                    l'offre est pré-remplie, vous la vérifiez avant de l'envoyer.
                  </p>
                  <a
                    href={`${API_URL}/api/entreprise/consultations/${activeConsultation.invitation_id}/cadre-dpgf.xlsx`}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-[#1E3A5F] text-[#1E3A5F] hover:bg-white"
                    data-testid="consultation-excel-export"
                  >
                    <DownloadSimple size={16} /> Télécharger le cadre Excel
                  </a>
                  <input ref={fichierRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleImportExcel} />
                  <Button variant="outline" className="rounded-none gap-2" disabled={importing}
                    onClick={() => fichierRef.current?.click()} data-testid="consultation-excel-import">
                    <UploadSimple size={16} /> {importing ? 'Import…' : "Importer l'Excel rempli"}
                  </Button>
                </div>
              )}
              {importResult && (importResult.erreurs?.length > 0 || importResult.avertissements?.length > 0) && (
                <div className={`border px-4 py-3 mb-4 text-sm ${importResult.ok ? 'border-[#D4880F] bg-[#FFFBEB]' : 'border-[#C53030] bg-[#FEF2F2]'}`}
                  data-testid="consultation-excel-resultat">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold">
                      {importResult.ok ? 'Import effectué, à vérifier :' : 'Le fichier n\'a pas été importé. Corrigez ces points puis importez-le à nouveau :'}
                    </p>
                    <button onClick={() => setImportResult(null)} className="text-[#6B6B6B]" aria-label="Fermer"><X size={14} /></button>
                  </div>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    {(importResult.erreurs || []).map((m, i) => <li key={`e${i}`} className="text-[#C53030]">{m}</li>)}
                    {(importResult.avertissements || []).map((m, i) => <li key={`a${i}`} className="text-[#8A5A0B]">{m}</li>)}
                  </ul>
                </div>
              )}

              {/* RP64 — téléphone : une carte par poste (le tableau à 8 colonnes est illisible) */}
              <div className="md:hidden space-y-3" data-testid="consultation-cartes-mobile">
                {dpgfData.lots.map((lot) => {
                  const refused = lotsRefuses.has(lot.id);
                  const lotTva = (typeof lot.tva_pct === 'number') ? lot.tva_pct : 20;
                  const lotHt = refused ? 0 : lot.postes.reduce((s, p) => {
                    const li = lignes.find((x) => x.poste_id === p.id);
                    return s + (p.quantite || 0) * (li?.prix_unitaire_ht || 0);
                  }, 0);
                  return (
                    <div key={lot.id} className="border border-[#E0E0DB] bg-white" data-testid={`consultation-carte-lot-${lot.id}`}>
                      <div className="bg-[#1E3A5F] text-white px-3 py-2 flex items-center gap-2">
                        <span className="flex-1 font-bold text-sm">{libNum(numLot(lot, dpgfData.lots.indexOf(lot)))} — {lot.nom}</span>
                        <span className="text-[10px] border border-white/30 px-1.5" title="TVA imposée par l'architecte">TVA {lotTva}%</span>
                      </div>
                      <label className="flex items-center gap-2 px-3 py-2 text-xs border-b border-[#F0F0EE]">
                        <input type="checkbox" checked={!refused} onChange={() => toggleRefuserLot(lot.id)} disabled={isSubmitted} />
                        {refused ? 'Je ne chiffre pas ce lot' : 'Je chiffre ce lot'}
                      </label>
                      {!refused && lot.postes.map((poste) => {
                        const ligne = lignes.find((l) => l.poste_id === poste.id) || {};
                        const pu = ligne.prix_unitaire_ht || 0;
                        return (
                          <div key={poste.id} className="px-3 py-2 border-b border-[#F0F0EE]" data-testid={`consultation-carte-${poste.id}`}>
                            <p className="text-sm text-[#2C2C2C]">{poste.designation}</p>
                            <p className="text-xs text-[#6B6B6B]">{(poste.quantite || 0).toLocaleString('fr-FR')} {poste.unite}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <label className="text-[11px] text-[#6B6B6B] whitespace-nowrap">PU HT</label>
                              <input type="number" inputMode="decimal" step="0.01" min="0" value={pu || ''}
                                onChange={(e) => updateLigne(poste.id, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                disabled={isSubmitted} placeholder="0,00"
                                className="flex-1 px-2 py-2 text-right text-base border border-[#E0E0DB] focus:border-[#C75B39] outline-none" />
                              <span className="text-sm font-medium whitespace-nowrap">
                                {((poste.quantite || 0) * pu).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="px-3 py-2 text-right text-sm font-bold bg-[#F5F5F3]">
                        {refused ? 'Non chiffré' : `Total du lot : ${lotHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT`}
                      </div>
                    </div>
                  );
                })}
                <div className="border-2 border-[#2C2C2C] bg-[#F5F5F3] px-3 py-2 text-sm">
                  <div className="flex justify-between"><span>Total HT</span><strong>{totals.ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                  <div className="flex justify-between"><span>Total TTC</span><strong className="text-[#1E3A5F]">{totals.ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                </div>
              </div>

              {/* Tableau type DPGFEditor (#14) — écran large */}
              <div className="hidden md:block border border-[#E0E0DB] bg-white" data-testid="consultation-table">
                <div className="grid grid-cols-[40px_1fr_70px_70px_100px_70px_110px_110px] bg-[#2C2C2C] text-white text-[10px] font-bold uppercase tracking-wider">
                  <div className="p-2">N°</div>
                  <div className="p-2">Désignation</div>
                  <div className="p-2">Unité</div>
                  <div className="p-2 text-right">Qté</div>
                  <div className="p-2 text-right">PU HT (€)</div>
                  <div className="p-2 text-right">TVA %</div>
                  <div className="p-2 text-right">Total HT (€)</div>
                  <div className="p-2 text-right">Total TTC (€)</div>
                </div>

                {dpgfData.lots.map((lot, lotIdx) => {
                  const refused = lotsRefuses.has(lot.id);
                  // TVA Sprint — Iter 79 : imposée par l'architecte, plus de saisie entreprise
                  const lotTva = (typeof lot.tva_pct === 'number') ? lot.tva_pct : 20;
                  const variante = variantes[lot.id] || { actif: false, description: '', montant_ht_total: 0 };
                  const lotHt = refused ? 0 : lot.postes.reduce((s, p) => {
                    const li = lignes.find((x) => x.poste_id === p.id);
                    return s + (p.quantite || 0) * (li?.prix_unitaire_ht || 0);
                  }, 0);

                  return (
                    <div key={lot.id} data-testid={`consultation-lot-row-${lot.id}`}>
                      {/* Lot header */}
                      <div className={`grid grid-cols-[40px_1fr_70px_70px_100px_70px_110px_110px] bg-[#1E3A5F] text-white items-center ${refused ? 'opacity-60' : ''}`}>
                        <div className="p-2 text-xs font-bold">{numLot(lot, lotIdx)}</div>
                        <div className="p-2 text-sm font-bold flex items-center gap-3 col-span-3">
                          <span className="flex-1 truncate">{lot.nom}</span>
                          {/* Toggle Concourir (#17) */}
                          <label className="flex items-center gap-1 text-[10px] cursor-pointer" title="Chiffrer ou ne pas chiffrer ce lot">
                            <input
                              type="checkbox"
                              checked={!refused}
                              onChange={() => toggleRefuserLot(lot.id)}
                              disabled={isSubmitted}
                              className="h-3 w-3 accent-white cursor-pointer"
                              data-testid={`consultation-lot-toggle-${lot.id}`}
                            />
                            {refused ? 'Je ne chiffre pas ce lot' : 'Je chiffre ce lot'}
                          </label>
                        </div>
                        <div className="p-2"></div>
                        {/* TVA — Iter 79 : lecture seule (imposée par l'architecte via snapshot DCE) */}
                        <div className="p-1 flex items-center justify-end" data-testid={`consultation-lot-tva-${lot.id}`}>
                          <span
                            className="px-2 py-0.5 text-[10px] text-white bg-white/10 border border-white/30"
                            title="TVA imposée par l'architecte"
                          >
                            {lotTva}%
                          </span>
                        </div>
                        <div className="p-2 text-right text-sm font-bold whitespace-nowrap">
                          {refused ? '—' : `${lotHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                        </div>
                        <div className="p-2"></div>
                      </div>

                      {/* Postes */}
                      {!refused && lot.postes.map((poste, posteIdx) => {
                        const ligne = lignes.find((l) => l.poste_id === poste.id) || {};
                        const pu = ligne.prix_unitaire_ht || 0;
                        const tvaPct = ligne.tva_pct ?? lotTva;
                        const lineHt = (poste.quantite || 0) * pu;
                        const lineTtc = lineHt * (1 + tvaPct / 100);
                        return (
                          <div key={poste.id} className="grid grid-cols-[40px_1fr_70px_70px_100px_70px_110px_110px] border-b border-[#F0F0EE] items-center hover:bg-[#FAFAF8]" data-testid={`consultation-poste-${poste.id}`}>
                            <div className="p-2 text-xs text-[#9A9A9A]">{numLot(lot, lotIdx)}.{posteIdx + 1}</div>
                            <div className="p-2">
                              <div className="text-sm text-[#2C2C2C]">{poste.designation}</div>
                              {poste.localisation_texte && (
                                <div className="text-[10px] text-[#9A9A9A] italic">📍 {poste.localisation_texte}</div>
                              )}
                              <Input
                                value={ligne.commentaire || ''}
                                onChange={(e) => updateLigne(poste.id, 'commentaire', e.target.value)}
                                disabled={isSubmitted}
                                placeholder="Commentaire (optionnel)"
                                className="rounded-none text-xs h-7 mt-1"
                                data-testid={`consultation-comment-${poste.id}`}
                              />
                            </div>
                            <div className="p-2 text-xs">{poste.unite}</div>
                            <div className="p-2 text-right text-xs">{(poste.quantite || 0).toLocaleString('fr-FR')}</div>
                            <div className="p-1">
                              <input type="number" step="0.01" min="0" value={pu || ''}
                                onChange={(e) => updateLigne(poste.id, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                disabled={isSubmitted}
                                placeholder="0.00"
                                className="w-full px-2 py-1 text-right text-sm border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent"
                                data-testid={`consultation-pu-${poste.id}`}
                              />
                            </div>
                            <div className="p-1 text-right text-[10px] text-[#9A9A9A]">{tvaPct}%</div>
                            <div className="p-2 text-right text-sm font-medium">{lineHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
                            <div className="p-2 text-right text-xs text-[#6B6B6B]">{lineTtc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
                          </div>
                        );
                      })}

                      {/* Section Variante (#20) */}
                      {!refused && (
                        <div className="border-b border-[#E0E0DB] bg-[#FAFAF8] px-3 py-2" data-testid={`consultation-variante-${lot.id}`}>
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox" checked={variante.actif}
                              onChange={() => toggleVariante(lot.id)}
                              disabled={isSubmitted}
                              className="h-3 w-3 accent-[#C75B39]"
                              data-testid={`consultation-variante-toggle-${lot.id}`}
                            />
                            <Lightning size={12} className="text-[#C75B39]" weight="fill" />
                            <span className="font-semibold text-[#2C2C2C]">Proposer une variante</span>
                          </label>
                          {variante.actif && (
                            <div className="mt-2 grid grid-cols-[1fr_140px] gap-2">
                              <Textarea
                                value={variante.description || ''}
                                onChange={(e) => updateVariante(lot.id, { description: e.target.value })}
                                disabled={isSubmitted}
                                placeholder="Description de la variante (matériaux, méthode, gain…)"
                                rows={2}
                                className="rounded-none text-xs"
                                data-testid={`consultation-variante-desc-${lot.id}`}
                              />
                              <div>
                                <label className="text-[10px] uppercase text-[#9A9A9A]">Montant variante HT</label>
                                <Input
                                  type="number" step="0.01"
                                  value={variante.montant_ht_total || 0}
                                  onChange={(e) => updateVariante(lot.id, { montant_ht_total: parseFloat(e.target.value) || 0 })}
                                  disabled={isSubmitted}
                                  className="rounded-none text-right text-sm"
                                  data-testid={`consultation-variante-montant-${lot.id}`}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {refused && (
                        <div className="border-b border-[#E0E0DB] bg-[#FFF3E0] px-3 py-2 text-xs text-[#C4773B] italic" data-testid={`consultation-lot-refused-${lot.id}`}>
                          Lot non soumis (vous avez choisi de ne pas concourir).
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Footer totals */}
                <div className="grid grid-cols-[40px_1fr_70px_70px_100px_70px_110px_110px] bg-[#F5F5F3] border-t-2 border-[#2C2C2C] items-center">
                  <div className="p-2"></div>
                  <div className="p-2 text-sm font-bold col-span-3">TOTAL GÉNÉRAL</div>
                  <div className="p-2"></div>
                  <div className="p-2 text-right text-[11px] text-[#6B6B6B]">{totals.tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € TVA</div>
                  <div className="p-2 text-right text-base font-bold text-[#2C2C2C]" data-testid="consultation-total-ht">
                    {totals.ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </div>
                  <div className="p-2 text-right text-base font-bold text-[#1E3A5F]" data-testid="consultation-total-ttc">
                    {totals.ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </div>
                </div>
                {totals.varHt > 0 && (
                  <div className="bg-[#FFF3E0] border-t border-[#C4773B] px-4 py-2 text-xs text-[#C4773B] flex items-center justify-between">
                    <span><Lightning size={12} weight="fill" className="inline mr-1" /> Variantes proposées (en supplément)</span>
                    <span className="font-bold">{totals.varHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT</span>
                  </div>
                )}
              </div>

              {/* Note + délai + mémoire */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#6B6B6B] block mb-1 font-semibold uppercase tracking-wide">Délai de réalisation (jours)</label>
                  <Input
                    type="number" min="0" value={delaiJours}
                    onChange={(e) => setDelaiJours(e.target.value)}
                    disabled={isSubmitted}
                    placeholder="ex : 60"
                    className="rounded-none"
                    data-testid="consultation-delai-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6B6B] block mb-1 font-semibold uppercase tracking-wide">Note globale</label>
                  <Input
                    value={noteGlobale}
                    onChange={(e) => setNoteGlobale(e.target.value)}
                    disabled={isSubmitted}
                    placeholder="Conditions particulières"
                    className="rounded-none"
                    data-testid="consultation-note-input"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="text-xs text-[#6B6B6B] block mb-1 font-semibold uppercase tracking-wide">Mémoire technique libre</label>
                <Textarea
                  value={memoireTechnique}
                  onChange={(e) => setMemoireTechnique(e.target.value)}
                  disabled={isSubmitted}
                  rows={3}
                  placeholder="Méthode, sous-traitants, garanties, références…"
                  className="rounded-none"
                  data-testid="consultation-memoire-textarea"
                />
              </div>

              {/* Actions */}
              {!isSubmitted && (
                <div className="mt-6 flex flex-wrap gap-3 justify-end border-t border-[#E0E0DB] pt-4">
                  <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="rounded-none gap-2" data-testid="save-brouillon-button">
                    <FloppyDisk size={16} />
                    {saving ? 'Sauvegarde…' : 'Sauvegarder brouillon'}
                  </Button>
                  <Button onClick={() => setRecapOpen(true)} disabled={saving} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2" data-testid="open-recap-button">
                    <PaperPlaneTilt size={16} weight="bold" /> Vérifier et envoyer
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Récap modal (#21) */}
          <Dialog open={recapOpen} onOpenChange={setRecapOpen}>
            <DialogContent className="max-w-2xl p-0" data-testid="consultation-recap-modal">
              <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Récapitulatif avant envoi</DialogTitle>
              </DialogHeader>
              <div className="p-4 space-y-3 max-h-[60vh] overflow-auto">
                <p className="text-sm text-[#6B6B6B]">Vérifiez votre offre avant envoi définitif. <strong>Une fois soumise, l'offre n'est plus modifiable.</strong></p>
                <div className="border border-[#E0E0DB]">
                  {dpgfData.lots.map((lot) => {
                    const refused = lotsRefuses.has(lot.id);
                    const lotHt = refused ? 0 : lot.postes.reduce((s, p) => {
                      const li = lignes.find((x) => x.poste_id === p.id);
                      return s + (p.quantite || 0) * (li?.prix_unitaire_ht || 0);
                    }, 0);
                    const v = variantes[lot.id];
                    return (
                      <div key={lot.id} className="px-3 py-2 border-b border-[#F0F0EE] flex items-center gap-3 text-sm" data-testid={`recap-lot-${lot.id}`}>
                        <span className="flex-1 truncate">{libNum(numLot(lot, dpgfData.lots.indexOf(lot)))} — {lot.nom}</span>
                        {refused ? (
                          <span className="text-[10px] uppercase font-bold text-[#C75B39]">Non soumis</span>
                        ) : (
                          <>
                            <span className="text-xs text-[#6B6B6B]">{lot.postes.length} poste{lot.postes.length > 1 ? 's' : ''}</span>
                            <span className="font-bold text-[#2C2C2C] whitespace-nowrap">{lotHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                            {v?.actif && (
                              <span className="text-[10px] uppercase font-bold text-[#C4773B]" title={v.description}>+ Variante {v.montant_ht_total?.toLocaleString('fr-FR')} €</span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="bg-[#F5F5F3] p-3 grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-[#9A9A9A]">Total HT base :</span> <strong className="ml-1">{totals.ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                  <div><span className="text-[#9A9A9A]">Total TVA :</span> <strong className="ml-1">{totals.tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                  <div><span className="text-[#9A9A9A]">Total TTC :</span> <strong className="ml-1 text-[#1E3A5F]">{totals.ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                  <div><span className="text-[#9A9A9A]">Variantes HT :</span> <strong className="ml-1 text-[#C4773B]">{totals.varHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></div>
                </div>
                <div className="text-xs text-[#6B6B6B] space-y-1">
                  <div><strong>{(dpgfData.reponse?.pieces_jointes || []).length}</strong> pièce(s) jointe(s)</div>
                  {delaiJours && <div>Délai proposé : <strong>{delaiJours} jours</strong></div>}
                </div>
              </div>
              <div className="border-t border-[#E0E0DB] bg-[#FAFAF8] px-4 py-3 flex justify-between">
                <Button variant="outline" onClick={() => setRecapOpen(false)} className="rounded-none" data-testid="recap-cancel-button">
                  <X size={14} className="mr-1" /> Retour modifier
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#2D6A4F] hover:bg-[#225538] rounded-none gap-2" data-testid="recap-confirm-button">
                  <PaperPlaneTilt size={16} weight="bold" /> Confirmer et envoyer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <CodeVerificationDialog
            open={codeOpen}
            onOpenChange={setCodeOpen}
            demanderUrl={`${API_URL}/api/acces/code`}
            demanderBody={{ action: 'offre' }}
            titre="Confirmer l'envoi de votre offre"
            onValider={(code) => handleSave(true, code)}
          />
        </div>
      </Layout>
    );
  }

  // ===== List view =====
  return (
    <Layout>
      <div className="p-6" data-testid="consultations-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Appels d'offres
          </h1>
          <p className="text-sm text-[#6B6B6B]">Les appels d'offres reçus pour vos lots : chiffrez-les avant la date limite</p>
        </div>

        {loading ? (
          <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        ) : consultations.length === 0 ? (
          <div className="card-kroquis text-center py-12">
            <ClipboardText size={48} className="text-[#D4A84B] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucune consultation</h3>
            <p className="text-sm text-[#6B6B6B]">Vous n'avez reçu aucun appel d'offres pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {consultations.map(c => {
              const cfg = STATUT_CONFIG[c.statut] || STATUT_CONFIG.EN_ATTENTE;
              const Icon = cfg.icon;
              const hasResponse = c.reponse_statut === 'SOUMISE';
              const res = c.resultat ? RESULTAT[c.resultat] : null;
              return (
                <div
                  key={c.invitation_id}
                  className="border border-[#E0E0DB] bg-white hover:border-[#2C2C2C] transition-colors cursor-pointer"
                  onClick={() => openConsultation(c)}
                  data-testid={`consultation-card-${c.invitation_id}`}
                >
                  <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-4">
                    <Icon size={24} className={cfg.color} />
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-base font-medium text-[#2C2C2C]">{c.chantier_nom}</p>
                      <p className="text-sm text-[#6B6B6B]">
                        {(c.lot_names || []).map((n, i) => `${libNum((c.lots_numeros || [])[i])} ${n}`.trim()).join(' · ') || c.lots_concernes?.join(', ')}
                      </p>
                      {/* A6 (P2#9) — décision lot par lot, dès qu'un lot est attribué */}
                      {Object.values(c.resultat_par_lot || {}).some(Boolean) && (
                        <p className="text-xs mt-1 text-[#2C2C2C]" data-testid={`consultation-resultat-lots-${c.invitation_id}`}>
                          {(c.lots_concernes || []).map((lid, i) => {
                            const r = (c.resultat_par_lot || {})[lid];
                            const nom = `${libNum((c.lots_numeros || [])[i])} ${(c.lot_names || [])[i] || ''}`.trim();
                            return `${nom} : ${r === 'retenu' ? 'Retenu' : r === 'non_retenu' ? 'Non retenu' : 'décision en attente'}`;
                          }).join(' · ')}
                        </p>
                      )}
                      {c.date_limite && (
                        <p className="text-xs text-[#C75B39] mt-1">
                          Date limite : {new Date(c.date_limite.length === 10 ? `${c.date_limite}T12:00:00` : c.date_limite).toLocaleDateString('fr-FR')}
                          {c.hors_delai && !hasResponse && ' — date passée (offre hors délai)'}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {res ? (
                        <span className={`text-xs px-3 py-1 font-bold border ${res.cls}`} data-testid={`consultation-resultat-${c.invitation_id}`}>
                          {res.label}
                        </span>
                      ) : (
                      <span className={`text-xs px-3 py-1 font-bold ${cfg.bg} ${cfg.color}`}>
                        {hasResponse ? 'Offre envoyée' : cfg.label}
                      </span>
                      )}
                      {c.montant_total_soumis != null && (
                        <p className="text-sm font-medium text-[#2D6A4F] mt-1">{c.montant_total_soumis.toLocaleString('fr-FR')} € HT</p>
                      )}
                    </div>
                    <ArrowRight size={16} className="text-[#9A9A9A]" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
